import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { apiClient, handleApiError, WeatherReport } from '../utils/api';
import { useReportsContext } from '../contexts/ReportsContext';
import Card, { CardHeader, CardContent, CardFooter } from '../components/Card';
import Input from '../components/Input';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import './ReportEditor.css';

interface ReportFormData {
  title: string;
  content: string;
}

interface FormErrors {
  title?: string;
  content?: string;
  general?: string;
}

const ReportEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { refreshReports } = useReportsContext();
  const isEditing = Boolean(id);
  const editorRef = useRef<any>(null);

  const [formData, setFormData] = useState<ReportFormData>({
    title: '',
    content: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setExistingReport] = useState<WeatherReport | null>(null);

  // Load existing report data if editing
  useEffect(() => {
    if (isEditing && id) {
      loadReport(id);
    }
  }, [isEditing, id]);

  const loadReport = async (reportId: string) => {
    setIsLoading(true);
    try {
      const report = await apiClient.getReport(reportId);
      setExistingReport(report);
      setFormData({
        title: report.title,
        content: report.content,
      });
    } catch (error) {
      setErrors({ general: handleApiError(error) });
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'El título es obligatorio';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'El título debe tener al menos 3 caracteres';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'El contenido es obligatorio';
    } else if (formData.content.trim().length < 10) {
      newErrors.content = 'El contenido debe tener al menos 10 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof ReportFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Image upload handler for TinyMCE
  const handleImageUpload = async (blobInfo: any): Promise<string> => {
    try {
      const file = blobInfo.blob();

      // Get presigned URL for upload
      const { uploadUrl, s3Key } = await apiClient.getReportUploadUrl(file);

      // Upload file to S3
      await apiClient.uploadFileToS3(uploadUrl, file);

      // Return the CloudFront URL for the uploaded image
      const cloudFrontUrl = `https://d1j3dotht1cg40.cloudfront.net/${s3Key}`;

      return cloudFrontUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Error al subir la imagen. Por favor, intenta nuevamente.');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      if (isEditing && id) {
        await apiClient.updateReport(id, {
          title: formData.title.trim(),
          content: formData.content.trim(),
        });
      } else {
        await apiClient.createReport({
          title: formData.title.trim(),
          content: formData.content.trim(),
        });
      }

      // Refresh the reports list to show the new/updated report
      await refreshReports();
      
      // Navigate back to admin dashboard on success
      navigate('/admin');
    } catch (error) {
      setErrors({ general: handleApiError(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin');
  };

  if (isLoading) {
    return (
      <div className="report-editor-loading">
        <LoadingSpinner />
        <p>Cargando informe...</p>
      </div>
    );
  }

  return (
    <div className="report-editor">
      <div className="report-editor-header">
        <h1>{isEditing ? 'Editar Informe' : 'Crear Nuevo Informe'}</h1>
        <p>
          {isEditing 
            ? 'Modifica los campos que desees actualizar' 
            : 'Completa la información del nuevo informe meteorológico'
          }
        </p>
      </div>

      <Card className="report-editor-card">
        <CardHeader>
          <h2>{isEditing ? 'Editar Informe Meteorológico' : 'Nuevo Informe Meteorológico'}</h2>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="report-form">
            {errors.general && (
              <div className="form-error" role="alert">
                {errors.general}
              </div>
            )}

            <div className="form-group">
              <Input
                type="text"
                label="Título del Informe"
                value={formData.title}
                onChange={handleInputChange('title')}
                error={errors.title}
                placeholder="Ej: Condiciones meteorológicas del 21 de diciembre"
                fullWidth
                disabled={isSubmitting}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="content" className="form-label">
                Contenido del Informe *
              </label>
              <Editor
                apiKey="djyysx6pkuaolkbbevwgcaiqzphkqxq7wbelyrssy5o08o8k"
                onInit={(_evt: any, editor: any) => editorRef.current = editor}
                value={formData.content}
                onEditorChange={(content: string) => {
                  setFormData(prev => ({ ...prev, content }));
                  if (errors.content) {
                    setErrors(prev => ({ ...prev, content: undefined }));
                  }
                }}
                init={{
                  height: 500,
                  menubar: true,
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | image media | help',
                  content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 14px; }',
                  images_upload_handler: handleImageUpload,
                  automatic_uploads: true,
                  file_picker_types: 'image',
                  image_advtab: true,
                  image_caption: true,
                  image_title: true,
                  paste_data_images: true,
                  readonly: isSubmitting,
                  placeholder: 'Describe las condiciones meteorológicas, observaciones, datos registrados, etc. Puedes arrastrar imágenes directamente aquí o usar el botón de imagen en la barra de herramientas.',
                }}
                disabled={isSubmitting}
              />
              {errors.content && (
                <span className="form-error-text">{errors.content}</span>
              )}
              <div className="form-help-text" style={{ marginTop: '8px' }}>
                Puedes arrastrar imágenes directamente al editor o hacer clic en el botón de imagen 📷 en la barra de herramientas. Las imágenes se subirán automáticamente.
              </div>
            </div>

          </form>
        </CardContent>

        <CardFooter>
          <div className="form-actions">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting 
                ? (isEditing ? 'Actualizando...' : 'Creando...') 
                : (isEditing ? 'Actualizar Informe' : 'Crear Informe')
              }
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ReportEditor;